import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/_ui/Accordion'
import { Alert, AlertDescription } from '@/components/_ui/Alert'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/_ui/Sheet'
import T, { useTranslate } from '@/components/Helpers/Translate'
import {
  getPickerRegions,
  languageTranslations,
} from '@/constants/pickerRegions/regions'
import { useHandleChangeLocale } from '@/lib/hooks/helpers/useHandleChangeLocale'
import { getObjectKeys } from '@/lib/utils/shared/getObjectKeys'
import Image from 'next/image'
import React from 'react'

type Props = {
  isCountrySelectorOpen: boolean
  setIsCountrySelectorOpen: (value: boolean) => void
}

const pickerRegions = getPickerRegions()

const regions = getObjectKeys(pickerRegions)

export const MobileCountrySelector = ({
  isCountrySelectorOpen,
  setIsCountrySelectorOpen,
}: Props) => {
  const { handleChangeLocale } = useHandleChangeLocale()

  const { translate } = useTranslate()

  const countriesAccordion = () => (
    <Accordion type="single" collapsible className="w-full">
      {regions.map((region) => {
        if (getObjectKeys(pickerRegions[region]).length === 0) {
          return null
        }

        return (
          <AccordionItem key={region} value={region}>
            <AccordionTrigger className="border-b">
              <T>{region}</T>
            </AccordionTrigger>
            <AccordionContent className="flex flex-col space-y-3 border-b py-5">
              {getObjectKeys(pickerRegions[region])
                .sort((a, b) => translate(a).localeCompare(translate(b)))
                .map((country) => {
                  const languages = [...pickerRegions[region][country]]

                  return (
                    <div className="flex space-x-3" key={country}>
                      <Image
                        src={`/flags/${country}.svg`}
                        alt="Country Flag"
                        height={30}
                        width={30}
                      />
                      <div>
                        <p
                          onClick={() => {
                            ;(async () => {
                              await handleChangeLocale({
                                newAlpha2: country,
                                newLanguage: languages[0], // default to first language
                              })
                            })().catch(() => {})
                          }}
                          className="cursor-pointer text-lg text-primary hover:underline"
                        >
                          <T>{country}</T>
                        </p>
                        <div className="flex space-x-3">
                          {languages.map((language) => (
                            <span
                              className="cursor-pointer text-xs text-slate-500 hover:underline"
                              onClick={() => {
                                ;(async () => {
                                  await handleChangeLocale({
                                    newAlpha2: country,
                                    newLanguage: language,
                                  })
                                })().catch(() => {})
                              }}
                              key={language}
                            >
                              <T>{languageTranslations[language]}</T>
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  )
                })}
            </AccordionContent>
          </AccordionItem>
        )
      })}
    </Accordion>
  )

  return (
    <Sheet open={isCountrySelectorOpen} onOpenChange={setIsCountrySelectorOpen}>
      <SheetContent className="w-full overflow-auto sm:max-w-full">
        <SheetHeader>
          <SheetTitle>
            <T>select_country</T>
          </SheetTitle>
          <SheetDescription>
            <Alert className="w-full py-2" variant="info">
              <AlertDescription>
                <p>
                  <T>changing_country</T>
                </p>
              </AlertDescription>
            </Alert>{' '}
          </SheetDescription>
        </SheetHeader>
        <div className="mt-5">{countriesAccordion()}</div>
      </SheetContent>
    </Sheet>
  )
}
